import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, Row, Col, Form, Button, Modal } from "react-bootstrap";
import { FaArrowLeft, FaEdit, FaSave, FaPlus, FaDownload, FaTrash } from "react-icons/fa";
import AsyncSelect from "react-select/async";
import { vehiculosService, transportistasService } from "../services/api";
import { toast } from "react-toastify";

const VehiculoDetail = () => {
  // helper para mostrar DD-MM-YYYY
  const formatDateDmy = (s) => {
    if (!s) return '-';
    // si ya viene como YYYY-MM-DD, convertir sin new Date para evitar TZ
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const [y, m, d] = s.split('-');
      return `${d}-${m}-${y}`;
    }
    const d = new Date(s);
    if (isNaN(d)) return s;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = d.getFullYear();
    return `${dd}-${mm}-${yy}`;
  };

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const vehiculoId = searchParams.get("vehiculo_id");
  const transportistaId = searchParams.get("transportista_id");
  const isCreate = !vehiculoId;

  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(isCreate);
  const [detalle, setDetalle] = useState({
    TRANSPORTISTA_ID: "",
    TIPO_VEHICULO: "",
    MATRICULA: "",
    OBSERVACIONES: "",
  });
  const [selectedTransportista, setSelectedTransportista] = useState(null);

  const [docTypes, setDocTypes] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [showDocModal, setShowDocModal] = useState(false);
  const [docTipoId, setDocTipoId] = useState("");
  const [docFechaFin, setDocFechaFin] = useState("");
  const [docHasFecha, setDocHasFecha] = useState(true);
  const [docFile, setDocFile] = useState(null);
  const [savingDoc, setSavingDoc] = useState(false);

  const [confirmDoc, setConfirmDoc] = useState({ show: false, id: null, label: '' });
  const [editDoc, setEditDoc] = useState({ show: false, id: null, tipoId: "", fechaFin: "", file: null });

  const [incidencias, setIncidencias] = useState([]);
  const [showIncModal, setShowIncModal] = useState(false);
  const [incEdit, setIncEdit] = useState({ id: null, fecha: "", esParte: false, observaciones: "" });
  const [confirmInc, setConfirmInc] = useState({ show: false, id: null });
  const showIncidencias = true;

  const [incFiles, setIncFiles] = useState([]);
  const [incNewFile, setIncNewFile] = useState(null);
  const [confirmIncFile, setConfirmIncFile] = useState({ show: false, id: null, label: '' });

  useEffect(() => {
    if (!isCreate) {
      loadDetail();
      loadDocuments();
      loadDocTypes();
      loadIncidencias();
    } else if (transportistaId) {
      setDetalle((prev) => ({ ...prev, TRANSPORTISTA_ID: transportistaId }));
      (async () => {
        try {
          const res = await transportistasService.getDetail(transportistaId);
          const t = res.data?.transportista;
          if (t) {
            const label = `${t.NOMBRE || ""} ${t.APELLIDOS || ""}`.trim() || String(transportistaId);
            setSelectedTransportista({ value: transportistaId, label });
          } else {
            setSelectedTransportista({ value: transportistaId, label: String(transportistaId) });
          }
        } catch (_e) {
          setSelectedTransportista({ value: transportistaId, label: String(transportistaId) });
        }
      })();
    }
    // eslint-disable-next-line
  }, [vehiculoId, isCreate, transportistaId]);

  const loadDetail = async () => {
    setLoading(true);
    try {
      const res = await vehiculosService.getDetail(vehiculoId);
      const v = res.data?.vehiculo;
      if (v) {
        setDetalle({
          TRANSPORTISTA_ID: v.TRANSPORTISTA_ID || "",
          TIPO_VEHICULO: v.TIPO_VEHICULO || "",
          MATRICULA: v.MATRICULA || "",
          OBSERVACIONES: v.OBSERVACIONES || "",
        });
        if (v.TRANSPORTISTA_ID) {
          setSelectedTransportista({ value: v.TRANSPORTISTA_ID, label: v.TRANSPORTISTA || String(v.TRANSPORTISTA_ID) });
        }
      }
    } catch (e) {
      toast.error("No se pudo cargar el vehículo");
    } finally {
      setLoading(false);
    }
  };

  const loadTransportistas = async (input) => {
    try {
      const res = await transportistasService.buscar({ search: input || "", page: 1, limit: 10, orderBy: 3 });
      const lista = res.data?.transportistas || [];
      return lista.map((t) => ({ value: t.ID, label: t.TRANSPORTISTA }));
    } catch (e) {
      return [];
    }
  };

  const loadDocTypes = async () => {
    try {
      const res = await vehiculosService.getDocumentTypes();
      setDocTypes(res.data?.types || []);
    } catch (_e) {
      setDocTypes([]);
    }
  };

  const loadDocuments = async () => {
    try {
      const res = await vehiculosService.listDocuments(vehiculoId);
      setDocuments(res.data?.documents || []);
    } catch (_e) {
      setDocuments([]);
    }
  };

  const loadIncidencias = async () => {
    try {
      const res = await vehiculosService.listIncidencias(vehiculoId);
      setIncidencias(res.data?.incidencias || []);
    } catch (_e) {
      setIncidencias([]);
    }
  };

  const loadIncidenciaFiles = async (incidenciaId) => {
    try {
      const res = await vehiculosService.listIncidenciaFiles(incidenciaId);
      setIncFiles(res.data?.files || []);
    } catch (_e) {
      setIncFiles([]);
    }
  };

  const handleChange = (field, value) => setDetalle((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    try {
      setLoading(true);
      // Preparar datos con valores por defecto para los campos eliminados
      const datosParaEnviar = {
        ...detalle,
        SEGURO: "", // String.Empty
        FECHA_FIN_SEGURO: null // null
      };

      if (isCreate) {
        const res = await vehiculosService.create(datosParaEnviar);
        if (res.data?.success) {
          toast.success("Vehículo creado");
          const nuevoId = res.data?.vehiculo?.ID || res.data?.vehiculo?.Id;
          if (nuevoId) {
            navigate(`/vehiculo-detail?vehiculo_id=${nuevoId}`);
          } else {
            // Si no devuelve ID, como fallback recargar
            window.location.reload();
          }
        } else toast.error(res.data?.message || "Error al crear");
      } else {
        const res = await vehiculosService.update(vehiculoId, datosParaEnviar);
        if (res.data?.success) {
          toast.success("Vehículo actualizado");
          setIsEditing(false);
          // Refrescar datos del detalle y documentación
          await loadDetail();
          await loadDocuments();
        } else toast.error(res.data?.message || "Error al actualizar");
      }
    } catch (e) {
      toast.error("Error en la operación");
    } finally {
      setLoading(false);
    }
  };

  const isFieldsLocked = useMemo(() => isCreate ? false : !isEditing, [isCreate, isEditing]);

  const handleUploadDoc = async (e) => {
    e.preventDefault();
    if (!docTipoId || (!docHasFecha ? false : !docFechaFin) || !docFile) {
      toast.warning('Selecciona tipo, fecha y fichero');
      return;
    }
    try {
      setSavingDoc(true);
      const res = await vehiculosService.uploadDocument(vehiculoId, docTipoId, docHasFecha ? docFechaFin : "", docFile);
      if (res.data?.success) {
        toast.success('Documento subido');
        setShowDocModal(false);
        setDocTipoId(""); setDocFechaFin(""); setDocFile(null); setDocHasFecha(true);
        await loadDocuments();
      } else toast.error(res.data?.message || 'Error al subir documento');
    } catch (_e) {
      toast.error('Error al subir documento');
    } finally {
      setSavingDoc(false);
    }
  };

  const handleDeleteDoc = async () => {
    try {
      await vehiculosService.deleteDocument(confirmDoc.id);
      setConfirmDoc({ show: false, id: null, label: '' });
      await loadDocuments();
    } catch (_e) {
      toast.error('Error eliminando documento');
    }
  };

  const handleDownloadDoc = async (docId, filename) => {
    try {
      const res = await vehiculosService.downloadDocument(docId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || `documento_${docId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (_e) {
      toast.error('Error descargando documento');
    }
  };

  const [editDocHasFecha, setEditDocHasFecha] = useState(true);
  const handleOpenEditDoc = (doc) => {
    setEditDoc({ show: true, id: doc.ID, tipoId: String(doc.ID_TIPO_DOCUMENTO), fechaFin: doc.FECHA_FIN ? new Date(doc.FECHA_FIN).toISOString().split('T')[0] : "", file: null });
    setEditDocHasFecha(!!doc.FECHA_FIN);
  };

  const handleSaveEditDoc = async (e) => {
    e.preventDefault();
    try {
      // Actualizar tipo/fecha
      await vehiculosService.updateDocument(editDoc.id, editDoc.tipoId, editDocHasFecha ? editDoc.fechaFin : "");
      // Si hay fichero nuevo, reemplazar
      if (editDoc.file) {
        await vehiculosService.replaceDocumentFile(editDoc.id, editDoc.file);
      }
      toast.success('Documento actualizado');
      setEditDoc({ show: false, id: null, tipoId: "", fechaFin: "", file: null });
      setEditDocHasFecha(true);
      await loadDocuments();
    } catch (_e) {
      toast.error('Error actualizando documento');
    }
  };

  const handleUploadIncFile = async (e) => {
    e.preventDefault();
    if (!incEdit.id) {
      toast.warning('Primero guarda la incidencia');
      return;
    }
    if (!incNewFile) {
      toast.warning('Selecciona un fichero');
      return;
    }
    try {
      await vehiculosService.uploadIncidenciaFile(incEdit.id, incNewFile);
      setIncNewFile(null);
      await loadIncidenciaFiles(incEdit.id);
    } catch (_e) {
      toast.error('Error subiendo fichero');
    }
  };

  const handleDeleteIncFile = async (fileId) => {
    try {
      await vehiculosService.deleteIncidenciaFile(fileId);
      await loadIncidenciaFiles(incEdit.id);
    } catch (_e) {
      toast.error('Error eliminando fichero');
    }
  };

  const handleDownloadIncFile = async (fileId, filename) => {
    try {
      const res = await vehiculosService.downloadIncidenciaFile(fileId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || `incidencia_${fileId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (_e) {
      toast.error('Error descargando fichero');
    }
  };

  const handleOpenCreateInc = () => {
    setIncEdit({ id: null, fecha: new Date().toISOString().split('T')[0], esParte: false, observaciones: "" });
    setIncFiles([]);
    setIncNewFile(null);
    setShowIncModal(true);
  };

  const handleOpenEditInc = async (inc) => {
    setIncEdit({
      id: inc.ID,
      fecha: inc.FECHA ? new Date(inc.FECHA).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      esParte: !!inc.ES_PARTE,
      observaciones: inc.OBSERVACIONES || "",
    });
    setIncNewFile(null);
    await loadIncidenciaFiles(inc.ID);
    setShowIncModal(true);
  };

  const handleSaveInc = async (e) => {
    e.preventDefault();
    try {
      if (incEdit.id) {
        await vehiculosService.updateIncidencia(incEdit.id, {
          fecha: incEdit.fecha,
          esParte: incEdit.esParte,
          observaciones: incEdit.observaciones,
        });
        toast.success('Incidencia actualizada');
      } else {
        const resp = await vehiculosService.createIncidencia({
          vehiculoId,
          transportistaId: detalle.TRANSPORTISTA_ID,
          fecha: incEdit.fecha,
          esParte: incEdit.esParte,
          observaciones: incEdit.observaciones,
        });
        const nueva = resp.data?.incidencia;
        if (nueva?.ID && incNewFile) {
          await vehiculosService.uploadIncidenciaFile(nueva.ID, incNewFile);
        }
        toast.success('Incidencia creada');
      }
      setShowIncModal(false);
      await loadIncidencias();
    } catch (_e) {
      toast.error('Error guardando incidencia');
    }
  };

  const handleDeleteInc = async () => {
    try {
      await vehiculosService.deleteIncidencia(confirmInc.id);
      setConfirmInc({ show: false, id: null });
      await loadIncidencias();
    } catch (_e) {
      toast.error('Error eliminando incidencia');
    }
  };

  return (
    <>
      <Card className="mt-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <Button variant="outline-secondary" onClick={() => navigate('/vehiculos')} className="me-3"><FaArrowLeft /></Button>
            <h4 className="mb-0">{isCreate ? 'Nuevo Vehículo' : 'Detalle de Vehículo'}</h4>
          </div>
          <div>
            {isCreate || isEditing ? (
              <Button variant="success" onClick={handleSave} disabled={loading} className="d-flex align-items-center gap-2"><FaSave /> {loading ? 'Guardando...' : 'Guardar'}</Button>
            ) : (
              <Button variant="primary" onClick={() => setIsEditing(true)} className="d-flex align-items-center"><FaEdit /></Button>
            )}
          </div>
        </Card.Header>
        <Card.Body>
          <Row className="mb-3">
            <Col md={6}>
              <div className="mb-3">
                <label className="form-label"><strong>Transportista:</strong></label>
                {isFieldsLocked ? (
                  <p className="mb-0">{selectedTransportista?.label || '-'}</p>
                ) : (
                  <AsyncSelect
                    cacheOptions
                    defaultOptions
                    openMenuOnFocus
                    isClearable
                    loadOptions={loadTransportistas}
                    value={selectedTransportista}
                    onChange={(opt) => {
                      setSelectedTransportista(opt);
                      handleChange('TRANSPORTISTA_ID', opt ? opt.value : '');
                    }}
                    placeholder="Buscar y seleccionar transportista..."
                    noOptionsMessage={() => 'Sin resultados'}
                  />
                )}
              </div>
            </Col>
            <Col md={3}>
              <div className="mb-3">
                <label className="form-label"><strong>Matrícula:</strong></label>
                {isFieldsLocked ? (
                  <p className="mb-0">{detalle.MATRICULA || '-'}</p>
                ) : (
                  <Form.Control value={detalle.MATRICULA} onChange={(e) => handleChange('MATRICULA', e.target.value)} />
                )}
              </div>
            </Col>
            <Col md={3}>
              <div className="mb-3">
                <label className="form-label"><strong>Tipo de vehículo:</strong></label>
                {isFieldsLocked ? (
                  <p className="mb-0">{detalle.TIPO_VEHICULO || '-'}</p>
                ) : (
                  <Form.Control value={detalle.TIPO_VEHICULO} onChange={(e) => handleChange('TIPO_VEHICULO', e.target.value)} />
                )}
              </div>
            </Col>
          </Row>



          <Row className="mb-3">
            <Col md={12}>
              <div className="mb-3">
                <label className="form-label"><strong>Observaciones:</strong></label>
                {isFieldsLocked ? (
                  <div style={{ whiteSpace: 'pre-wrap' }}>{detalle.OBSERVACIONES || '-'}</div>
                ) : (
                  <Form.Control as="textarea" rows={5} value={detalle.OBSERVACIONES} onChange={(e) => handleChange('OBSERVACIONES', e.target.value)} />
                )}
              </div>
            </Col>
          </Row>

          {/* Modals */}
          <Modal show={showDocModal} onHide={() => setShowDocModal(false)} centered>
            <Form onSubmit={handleUploadDoc}>
              <Modal.Header closeButton><Modal.Title>Subir documento</Modal.Title></Modal.Header>
              <Modal.Body>
                <div className="mb-3">
                  <label className="form-label">Tipo de documento</label>
                  <Form.Select value={docTipoId} onChange={(e) => setDocTipoId(e.target.value)} required>
                    <option value="">Selecciona tipo</option>
                    {docTypes.map((t) => (
                      <option key={t.ID} value={t.ID}>{t.NOMBRE}</option>
                    ))}
                  </Form.Select>
                </div>
                <div className="mb-2">
                  <Form.Check
                    type="checkbox"
                    label="Tiene fecha de validez"
                    checked={docHasFecha}
                    onChange={(e) => setDocHasFecha(e.target.checked)}
                  />
                </div>
                {docHasFecha && (
                  <div className="mb-3">
                    <label className="form-label">Fecha de validez</label>
                    <Form.Control type="date" value={docFechaFin} onChange={(e) => setDocFechaFin(e.target.value)} required={docHasFecha} />
                  </div>
                )}
                <div className="mb-3">
                  <label className="form-label">Fichero (PDF)</label>
                  <Form.Control type="file" accept="application/pdf" onChange={(e) => setDocFile(e.target.files?.[0] || null)} required />
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowDocModal(false)}>Cancelar</Button>
                <Button type="submit" variant="primary" disabled={savingDoc}>{savingDoc ? 'Subiendo...' : 'Subir'}</Button>
              </Modal.Footer>
            </Form>
          </Modal>

          <Modal show={confirmDoc.show} onHide={() => setConfirmDoc({ show: false, id: null, label: '' })} centered>
            <Modal.Header closeButton><Modal.Title>Eliminar documento</Modal.Title></Modal.Header>
            <Modal.Body>¿Seguro que deseas eliminar "{confirmDoc.label}"?</Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setConfirmDoc({ show: false, id: null, label: '' })}>Cancelar</Button>
              <Button variant="danger" onClick={handleDeleteDoc}>Eliminar</Button>
            </Modal.Footer>
          </Modal>

          <Modal show={editDoc.show} onHide={() => { setEditDoc({ show: false, id: null, tipoId: "", fechaFin: "", file: null }); setEditDocHasFecha(true); }} centered>
            <Form onSubmit={handleSaveEditDoc}>
              <Modal.Header closeButton><Modal.Title>Editar documento</Modal.Title></Modal.Header>
              <Modal.Body>
                <div className="mb-3">
                  <label className="form-label">Tipo de documento</label>
                  <Form.Select value={editDoc.tipoId} onChange={(e) => setEditDoc((prev) => ({ ...prev, tipoId: e.target.value }))} required>
                    <option value="">Selecciona tipo</option>
                    {docTypes.map((t) => (
                      <option key={t.ID} value={t.ID}>{t.NOMBRE}</option>
                    ))}
                  </Form.Select>
                </div>
                <div className="mb-2">
                  <Form.Check
                    type="checkbox"
                    label="Tiene fecha de validez"
                    checked={editDocHasFecha}
                    onChange={(e) => setEditDocHasFecha(e.target.checked)}
                  />
                </div>
                {editDocHasFecha && (
                  <div className="mb-3">
                    <label className="form-label">Fecha de validez</label>
                    <Form.Control type="date" value={editDoc.fechaFin} onChange={(e) => setEditDoc((prev) => ({ ...prev, fechaFin: e.target.value }))} required={editDocHasFecha} />
                  </div>
                )}
                <div className="mb-3">
                  <label className="form-label">Reemplazar fichero (PDF)</label>
                  <Form.Control type="file" accept="application/pdf" onChange={(e) => setEditDoc((prev) => ({ ...prev, file: (e.target.files?.[0] || null) }))} />
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => { setEditDoc({ show: false, id: null, tipoId: "", fechaFin: "", file: null }); setEditDocHasFecha(true); }}>Cancelar</Button>
                <Button type="submit" variant="primary">Guardar</Button>
              </Modal.Footer>
            </Form>
          </Modal>

          {/* Modal Incidencia */}
          <Modal show={showIncModal} onHide={() => setShowIncModal(false)} centered>
            <Form onSubmit={handleSaveInc}>
              <Modal.Header closeButton><Modal.Title>{incEdit.id ? 'Editar incidencia' : 'Nueva incidencia'}</Modal.Title></Modal.Header>
              <Modal.Body>
                <div className="mb-3">
                  <label className="form-label">Fecha de incidencia</label>
                  <Form.Control type="date" value={incEdit.fecha} onChange={(e) => setIncEdit((p) => ({ ...p, fecha: e.target.value }))} required />
                </div>
                <div className="mb-3">
                  <Form.Check type="checkbox" label="Es parte" checked={incEdit.esParte} onChange={(e) => setIncEdit((p) => ({ ...p, esParte: e.target.checked }))} />
                </div>
                <div className="mb-3">
                  <label className="form-label">Observaciones</label>
                  <Form.Control as="textarea" rows={4} value={incEdit.observaciones} onChange={(e) => setIncEdit((p) => ({ ...p, observaciones: e.target.value }))} />
                </div>

                {/* Subir fichero nuevo para incidencia (en creación o edición) */}
                <div className="mb-2">
                  <label className="form-label">Añadir fichero (PDF)</label>
                  <Form.Control type="file" accept="application/pdf" onChange={(e) => setIncNewFile(e.target.files?.[0] || null)} />
                  {incEdit.id && (
                    <div className="mt-2">
                      <Button variant="outline-secondary" size="sm" onClick={handleUploadIncFile} disabled={!incNewFile}>Subir fichero</Button>
                    </div>
                  )}
                </div>

                {/* Ficheros de la incidencia */}
                {incEdit.id && (
                  <div className="mb-3">
                    <label className="form-label"><strong>Ficheros</strong></label>
                    <div className="table-responsive">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>Nombre</th>
                            <th>Fecha</th>
                            <th>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {incFiles.length === 0 ? (
                            <tr><td colSpan={3} className="text-center">Sin ficheros</td></tr>
                          ) : (
                            incFiles.map((f) => {
                              const display = f.FICHERO?.substring(f.FICHERO.indexOf('_') + 1) || f.FICHERO;
                              return (
                                <tr key={f.ID}>
                                  <td>{display}</td>
                                  <td>{f.FECHA ? new Date(f.FECHA).toLocaleDateString('es-ES') : '-'}</td>
                                  <td>
                                    <div className="d-flex gap-2">
                                      <Button size="sm" variant="outline-primary" onClick={() => handleDownloadIncFile(f.ID, f.FICHERO)} title="Descargar"><FaDownload /></Button>
                                      <Button size="sm" variant="outline-danger" onClick={() => setConfirmIncFile({ show: true, id: f.ID, label: display })} title="Eliminar"><FaTrash /></Button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowIncModal(false)}>Cancelar</Button>
                <Button type="submit" variant="primary">Guardar</Button>
              </Modal.Footer>
            </Form>
          </Modal>

          {/* Confirmar eliminar incidencia */}
          {showIncidencias && (
            <Modal show={confirmInc.show} onHide={() => setConfirmInc({ show: false, id: null })} centered>
              <Modal.Header closeButton><Modal.Title>Eliminar incidencia</Modal.Title></Modal.Header>
              <Modal.Body>¿Seguro que deseas eliminar esta incidencia?</Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setConfirmInc({ show: false, id: null })}>Cancelar</Button>
                <Button variant="danger" onClick={handleDeleteInc}>Eliminar</Button>
              </Modal.Footer>
            </Modal>
          )}

          {/* Confirmar eliminar fichero de incidencia */}
          <Modal show={confirmIncFile.show} onHide={() => setConfirmIncFile({ show: false, id: null, label: '' })} centered>
            <Modal.Header closeButton><Modal.Title>Eliminar fichero</Modal.Title></Modal.Header>
            <Modal.Body>¿Seguro que deseas eliminar "{confirmIncFile.label}"?</Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setConfirmIncFile({ show: false, id: null, label: '' })}>Cancelar</Button>
              <Button variant="danger" onClick={async () => { await handleDeleteIncFile(confirmIncFile.id); setConfirmIncFile({ show: false, id: null, label: '' }); }}>Eliminar</Button>
            </Modal.Footer>
          </Modal>

        </Card.Body>
      </Card>

      {!isCreate && (
        <Card className="mt-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Documentación</h5>
            <Button variant="outline-success" className="d-flex align-items-center gap-2" onClick={() => setShowDocModal(true)}>
              <FaPlus /> Añadir documento
            </Button>
          </Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <table className="table table-striped table-bordered">
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Fecha subida</th>
                    <th>Validez hasta</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.length === 0 ? (
                    <tr><td colSpan={4} className="text-center">Sin documentos</td></tr>
                  ) : (
                    documents.map((d) => (
                      <tr key={d.ID}>
                        <td>{d.TIPO_NOMBRE}</td>
                        <td>{d.CREATE_DATE ? new Date(d.CREATE_DATE).toLocaleDateString('es-ES') : '-'}</td>
                        <td>{d.FECHA_FIN ? new Date(d.FECHA_FIN).toLocaleDateString('es-ES') : '-'}</td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button size="sm" variant="outline-primary" onClick={() => handleDownloadDoc(d.ID, d.FICHERO)} title="Descargar"><FaDownload /></Button>
                            <Button size="sm" variant="outline-secondary" onClick={() => handleOpenEditDoc(d)} title="Editar"><FaEdit /></Button>
                            <Button size="sm" variant="outline-danger" onClick={() => setConfirmDoc({ show: true, id: d.ID, label: d.TIPO_NOMBRE })} title="Eliminar"><FaTrash /></Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card.Body>
        </Card>
      )}

      {!isCreate && showIncidencias && (
        <Card className="mt-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Incidencias</h5>
            <Button variant="outline-success" className="d-flex align-items-center gap-2" onClick={handleOpenCreateInc}>
              <FaPlus /> Añadir incidencia
            </Button>
          </Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <table className="table table-striped table-bordered">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Es parte</th>
                    <th>Observaciones</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {incidencias.length === 0 ? (
                    <tr><td colSpan={4} className="text-center">Sin incidencias</td></tr>
                  ) : (
                    incidencias.map((i) => (
                      <tr key={i.ID}>
                        <td>{i.FECHA ? new Date(i.FECHA).toLocaleDateString('es-ES') : '-'}</td>
                        <td>{i.ES_PARTE ? 'Sí' : 'No'}</td>
                        <td style={{ whiteSpace: 'pre-wrap' }}>{i.OBSERVACIONES || '-'}</td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button size="sm" variant="outline-secondary" onClick={() => handleOpenEditInc(i)} title="Editar"><FaEdit /></Button>
                            <Button size="sm" variant="outline-danger" onClick={() => setConfirmInc({ show: true, id: i.ID })} title="Eliminar"><FaTrash /></Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export default VehiculoDetail;


